/**
 * DatePickerInput — themed drop-in replacement for `<input type="date">`.
 *
 * Why we replaced the native input:
 *   The browser's built-in date picker can barely be styled. Chrome lets you
 *   tweak a few pseudo-elements; Safari and mobile browsers give you nothing.
 *   The result is a calendar that looks alien against the rest of the app's
 *   Option D styling (rounded pills, indigo gradient, soft slate shadows).
 *
 * Public API mirrors the native input so existing call-sites swap cleanly:
 *   <DatePickerInput value="2025-05-21" onChange={(v) => …} placeholder="…" />
 *
 * Props:
 *   value           — ISO date string `YYYY-MM-DD`, or empty string when unset
 *   onChange(v)     — fires with the new ISO date string (or empty when cleared)
 *   placeholder     — text shown in the trigger when no date is picked
 *   min / max       — optional ISO date strings to restrict the picker range
 *   disabled        — disables the trigger and the calendar
 *   className       — extra class on the trigger button
 *   align           — Radix Popover align: 'start' | 'center' | 'end' (default 'start')
 *
 * Implementation:
 *   - Radix Popover for outside-click + escape-key + a11y plumbing
 *   - react-day-picker for the actual calendar grid (8.10, already a dep)
 *   - Day cells, nav, weekday header all themed via the `.dpi-*` CSS classes
 *     added in App.css
 *   - date-fns for the ISO ↔ Date conversion and the trigger display label
 *
 * Accessibility:
 *   The trigger is a regular <button>; the calendar is rendered inside a Radix
 *   Popover which handles focus trapping + ESC dismissal. Each day cell is a
 *   button with an aria-label (provided by react-day-picker).
 */
import React, { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import * as Popover from '@radix-ui/react-popover';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
// react-day-picker ships a small base stylesheet that handles the table grid
// layout. Our themed overrides (in App.css under the .dpi-* selectors) replace
// the visual styling but rely on this base for the day-cell positioning.
import 'react-day-picker/dist/style.css';

const ISO_FMT = 'yyyy-MM-dd';
const DISPLAY_FMT = 'MMM d, yyyy'; // "May 27, 2026"

/** Parse a `YYYY-MM-DD` string into a local-midnight Date (or null). */
const parseValue = (raw) => {
	if (!raw) return null;
	const d = parseISO(raw);
	return isValid(d) ? d : null;
};

const DatePickerInput = ({
	value = '',
	onChange,
	placeholder = 'Pick a date',
	min,
	max,
	disabled = false,
	className = '',
	align = 'start',
}) => {
	const [open, setOpen] = useState(false);
	const selected = parseValue(value);
	const minDate = parseValue(min) || undefined;
	const maxDate = parseValue(max) || undefined;

	const handleSelect = (day) => {
		if (!day) {
			// react-day-picker passes undefined when the user clicks the
			// already-selected day. Treat as "clear" — useful + matches native.
			onChange?.('');
			return;
		}
		onChange?.(format(day, ISO_FMT));
		// Auto-dismiss after pick — feels more like the native widget.
		setOpen(false);
	};

	const triggerLabel = selected ? format(selected, DISPLAY_FMT) : placeholder;

	return (
		<Popover.Root open={open} onOpenChange={setOpen}>
			<Popover.Trigger asChild>
				<button
					type="button"
					disabled={disabled}
					className={`dpi-trigger ${selected ? 'is-filled' : 'is-empty'} ${className}`}
				>
					<CalendarIcon size={14} className="dpi-trigger__icon" />
					<span className="dpi-trigger__label">{triggerLabel}</span>
					{selected && !disabled && (
						<span
							role="button"
							tabIndex={-1}
							aria-label="Clear date"
							className="dpi-trigger__clear"
							onClick={(e) => {
								// Stop the click from also opening the popover.
								e.stopPropagation();
								onChange?.('');
							}}
						>
							<X size={12} />
						</span>
					)}
				</button>
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content
					className="dpi-popover"
					align={align}
					side="bottom"
					sideOffset={6}
					collisionPadding={20}
					avoidCollisions
					sticky="partial"
					onOpenAutoFocus={(e) => {
						// Don't yank focus into the day grid on open — keeps the
						// picker feeling lightweight, lets the user click without
						// the calendar stealing focus mid-typing elsewhere.
						e.preventDefault();
					}}
				>
					<DayPicker
						mode="single"
						selected={selected || undefined}
						onSelect={handleSelect}
						fromDate={minDate}
						toDate={maxDate}
						showOutsideDays
						weekStartsOn={0}
						classNames={{
							months: 'dpi-months',
							month: 'dpi-month',
							caption: 'dpi-caption',
							caption_label: 'dpi-caption-label',
							nav: 'dpi-nav',
							nav_button: 'dpi-nav-btn',
							nav_button_previous: 'dpi-nav-btn--prev',
							nav_button_next: 'dpi-nav-btn--next',
							table: 'dpi-table',
							head_row: 'dpi-head-row',
							head_cell: 'dpi-head-cell',
							row: 'dpi-row',
							cell: 'dpi-cell',
							day: 'dpi-day',
							day_selected: 'dpi-day--selected',
							day_today: 'dpi-day--today',
							day_outside: 'dpi-day--outside',
							day_disabled: 'dpi-day--disabled',
							day_hidden: 'dpi-day--hidden',
						}}
					/>
					{/* Footer with Clear / Today shortcuts (matches the screenshot
					    the user shared but in our Option D styling). */}
					<div className="dpi-footer">
						<button
							type="button"
							className="dpi-footer-btn"
							onClick={() => {
								onChange?.('');
								setOpen(false);
							}}
						>
							Clear
						</button>
						<button
							type="button"
							className="dpi-footer-btn dpi-footer-btn--primary"
							onClick={() => {
								onChange?.(format(new Date(), ISO_FMT));
								setOpen(false);
							}}
						>
							Today
						</button>
					</div>
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
};

export default DatePickerInput;
